import { Link } from "react-router-dom";
import { Wordmark } from "../components/Wordmark";
import { ArrowLeft, Shield } from "lucide-react";

const Section = ({ title, children }) => (
  <section className="mt-8">
    <h2 className="font-serif text-2xl text-charcoal">{title}</h2>
    <div className="mt-3 space-y-3 text-charcoal/85 leading-relaxed">{children}</div>
  </section>
);

export default function Privacy() {
  return (
    <div className="min-h-screen grain bg-cream" data-testid="privacy-page">
      <header className="sticky top-0 z-30 bg-cream/85 backdrop-blur-md border-b border-line">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="privacy-home-link">
            <img src="/logo.png" alt="Write My Story" className="h-9 w-9 object-contain" />
            <Wordmark size="text-lg sm:text-xl" />
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-subtle hover:text-charcoal text-sm">
            <ArrowLeft size={15} /> Home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 relative z-10">
        <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-sage font-semibold">
          <Shield size={14} /> Your privacy
        </span>
        <h1 className="font-serif text-4xl text-charcoal mt-3">Privacy Policy</h1>
        <p className="text-subtle mt-2">Last updated: June 2026</p>

        <p className="mt-6 text-charcoal/85 leading-relaxed">
          Write My Story ("we", "us") is a memoir-writing service created by Juliet Sullivan. This policy
          explains what personal information we collect, how we use it, and the choices you have. We are the
          data controller for the information you provide. If you have any questions, contact us at{" "}
          <a href="mailto:julietsullivan@outlook.com" className="text-teal underline">julietsullivan@outlook.com</a>.
        </p>

        <Section title="Information we collect">
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>Account details</strong> — your email address and an encrypted (hashed) password.</li>
            <li><strong>Profile details</strong> — information you choose to add, such as a preferred name and birth year.</li>
            <li><strong>Your memories</strong> — the text you write, voice recordings you make, transcripts of those recordings, photos you upload, and the chapters and books generated from them.</li>
            <li><strong>Family contributions</strong> — memories, names and photos that people you invite choose to share about you.</li>
            <li><strong>Payment information</strong> — handled securely by Stripe. We do not see or store your full card details.</li>
            <li><strong>Basic technical data</strong> — such as log data needed to keep the service secure and working.</li>
          </ul>
        </Section>

        <Section title="How we use your information">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>To provide the service — storing your memories and turning them into written prose, chapters and a book.</li>
            <li>To transcribe voice recordings and generate follow-up questions and drafts using trusted AI providers.</li>
            <li>To manage your account, subscription and free trial, and to send service emails (such as password resets and, if enabled, gentle reminders).</li>
            <li>To keep the service secure and to comply with our legal obligations.</li>
          </ul>
          <p>Our lawful bases under UK GDPR are performance of our contract with you, your consent (which you can withdraw), and our legitimate interests in running a secure service.</p>
        </Section>

        <Section title="Sharing with trusted providers">
          <p>We do not sell your information. We share it only with service providers who help us run Write My Story, under appropriate safeguards:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>OpenAI</strong> — to transcribe recordings and generate memoir text. Your content is processed to create your drafts.</li>
            <li><strong>Stripe</strong> — to process subscription payments securely.</li>
            <li><strong>Resend</strong> — to send service emails.</li>
            <li><strong>Our hosting and database infrastructure</strong> — to store your data securely.</li>
          </ul>
        </Section>

        <Section title="Storage, security and retention">
          <p>Your data is stored securely and access is restricted. Your memories are private to your account by default and are never publicly shared by us. We keep your information for as long as your account is active. If you delete your account, we delete your associated content.</p>
        </Section>

        <Section title="Your rights">
          <p>Under UK GDPR you have the right to access, correct, delete or export your personal data, and to object to or restrict certain processing. You can delete your account and its data at any time from your Settings, or contact us to exercise any of these rights. You also have the right to complain to the UK Information Commissioner's Office (ICO).</p>
        </Section>

        <Section title="Cookies">
          <p>We use a small number of essential cookies and similar storage to keep you signed in and to keep the service secure. We do not use them for advertising.</p>
        </Section>

        <Section title="Children">
          <p>Write My Story is intended for adults. It is not directed at children under 16.</p>
        </Section>

        <Section title="Changes to this policy">
          <p>We may update this policy from time to time. When we do, we'll change the "last updated" date above, and where appropriate let you know within the app.</p>
        </Section>

        <Section title="Contact">
          <p>Questions about your privacy? Email <a href="mailto:julietsullivan@outlook.com" className="text-teal underline">julietsullivan@outlook.com</a>.</p>
          <p>Write My Story is a memoir service by author Juliet Sullivan.<br />
          275 New North Road, London, N1 7AA, United Kingdom.</p>
        </Section>

        <div className="mt-10 pt-6 border-t border-line text-sm text-subtle">
          See also our <Link to="/terms" className="text-teal underline">Terms of Service</Link>.
        </div>
      </main>
    </div>
  );
}
